import { ref, reactive } from 'vue';
import { getUploadConfig, directUpload } from '@/request/common';

export interface UploadOptions {
  onProgress?: (progress: number, file: File) => void;
  onSuccess?: (response: any, file: File) => void;
  onError?: (error: Error, file: File) => void;
  beforeUpload?: (file: File) => boolean | Promise<boolean>;
  accept?: string;
  multiple?: boolean;
}

export function useUpload(options: UploadOptions = {}) {
  const uploading = ref(false);
  const fileList = reactive<File[]>([]);

  // 上传状态管理
  const uploadStatus = reactive<
    Record<
      string,
      {
        progress: number;
        status: 'ready' | 'uploading' | 'success' | 'error';
        response?: any;
        error?: Error;
      }
    >
  >({});

  // 处理文件上传
  const upload = async (files: File | File[]) => {
    const fileArray = Array.isArray(files) ? files : [files];

    for (const file of fileArray) {
      try {
        // 前置校验
        if (options.beforeUpload) {
          const valid = await options.beforeUpload(file);
          if (!valid) continue;
        }

        uploading.value = true;
        uploadStatus[file.name] = {
          progress: 0,
          status: 'uploading',
        };

        // 获取上传配置
        const uploadConfigRes = await getUploadConfig(file);
        const { code, data, msg } = uploadConfigRes.data;

        if (code !== 200) {
          throw new Error(msg);
        }

        const { presignedUrl, url, fileId } = data;

        // 执行上传
        const uploadRes = await directUpload(presignedUrl, file);

        if (uploadRes.status === 200) {
          uploadStatus[file.name] = {
            progress: 100,
            status: 'success',
            response: {
              url,
              id: fileId,
              originalFilename: file.name,
            },
          };
          options.onSuccess?.(uploadStatus[file.name].response, file);
        }
      } catch (error) {
        uploadStatus[file.name] = {
          progress: 0,
          status: 'error',
          error: error as Error,
        };
        options.onError?.(error as Error, file);
      }
    }

    uploading.value = false;
  };

  // 手动触发上传
  const triggerUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = options.accept || '';
    input.multiple = options.multiple || false;

    input.onchange = async (e) => {
      const files = (e.target as HTMLInputElement).files;
      if (files) {
        await upload(Array.from(files));
      }
    };

    input.click();
  };

  // 重置状态
  const reset = () => {
    uploading.value = false;
    Object.keys(uploadStatus).forEach((key) => {
      delete uploadStatus[key];
    });
  };

  return {
    upload,
    triggerUpload,
    reset,
    uploading,
    uploadStatus,
    fileList,
  };
}
